<template>
  <div ref="container" data-walkthrough-id="quick-search-modal" @click.stop="">
    <div
      :class="animationClass()"
      class="sf-search sf-layer-white sf-layer-shadow sf-below-3"
      @click="$refs.searchInput.focus()"
    >
      <input
        ref="searchInput"
        v-model="currentQueryInput"
        v-focus="focused"
        :placeholder="I18n.t('js.quick_search.search_for')"
        class="
          sf-search-input sf-form-input sf-form-input-large sf-no-border sf-full
        "
        type="text"
        @blur="inputBlurred"
        @input="updateCurrentQuery"
        @keydown.down="moveSelectionDown"
        @keydown.up="moveSelectionUp"
        @keyup.enter="openActiveSelection"
        @keyup.esc="clearCurrentQuery"
      />
    </div>
    <div :style="resultsStyle">
      <div
        v-if="searchResultsByType('Workflow').length > 0"
        class="
          sf-search-results sf-layer-white sf-layer-shadow
          search-form__results-section
        "
      >
        <div class="sf-tab sf-tab-icons sf-tab-full">
          <div class="sf-tab-label">
            {{ I18n.t('js.quick_search.workflows') }}
          </div>
          <div
            v-for="workflow in searchResultsByType('Workflow')"
            :key="workflow.id"
            :class="activeSelectionClass(workflow)"
            class="sf-search-item sf-tab-item"
            @click="openItem(workflow, 'click')"
          >
            <a :href="urlForWorkflow(workflow)" class="sf-a">{{
              workflow.name
            }}</a>
          </div>
          <div
            v-if="extraSearchResultsCountsByType('Workflow') > 0"
            class="sf-tab-item sf-bg-white sf-c-text sf-dim-el"
          >
            {{
              I18n.t('js.quick_search.x_more_results', {
                count: extraSearchResultsCountsByType('Workflow'),
              })
            }}
          </div>
        </div>
      </div>
      <div
        v-if="searchResultsByType('ReconciliationText').length > 0"
        class="
          sf-search-results sf-layer-white sf-layer-shadow
          search-form__results-section
        "
      >
        <div class="sf-tab sf-tab-icons sf-tab-full">
          <div class="sf-tab-label">
            {{ I18n.t('js.quick_search.reconciliation_texts') }}
          </div>
          <div
            v-for="reconciliationText in searchResultsByType(
              'ReconciliationText'
            )"
            :key="reconciliationText.id"
            :class="activeSelectionClass(reconciliationText)"
            class="sf-search-item sf-tab-item"
            @click="openItem(reconciliationText, 'click')"
          >
            <span
              v-if="reconciliationText.starred"
              class="sf-tab-item-icon sf-c-warning"
            >
              <span class="fa fa-star"></span>
            </span>
            <a
              :href="urlForReconciliationText(reconciliationText)"
              class="sf-a"
              >{{ reconciliationText.name }}</a
            >
          </div>
          <div
            v-if="extraSearchResultsCountsByType('ReconciliationText') > 0"
            class="sf-tab-item sf-bg-white sf-c-text sf-dim-el"
          >
            {{
              I18n.t('js.quick_search.x_more_results', {
                count: extraSearchResultsCountsByType('ReconciliationText'),
              })
            }}
          </div>
        </div>
      </div>
      <div
        v-if="searchResultsByType('Account').length > 0"
        class="
          sf-search-results sf-layer-white sf-layer-shadow
          search-form__results-section
        "
      >
        <div class="sf-tab sf-tab-icons sf-tab-full">
          <div class="sf-tab-label">
            {{ I18n.t('js.quick_search.accounts') }}
          </div>
          <div
            v-for="account in searchResultsByType('Account')"
            :key="account.id"
            :class="activeSelectionClass(account)"
            class="sf-search-item sf-tab-item"
            @click="openItem(account, 'click')"
          >
            <span v-if="account.starred" class="sf-tab-item-icon sf-c-warning">
              <span class="fa fa-star"></span>
            </span>
            <a :href="urlForAccount(account)" class="sf-a">
              {{ account.name }}
              <span class="muted">
                {{ account.original_number }}
              </span>
            </a>
          </div>
          <div
            v-if="extraSearchResultsCountsByType('Account') > 0"
            class="sf-tab-item sf-bg-white sf-c-text sf-dim-el"
          >
            {{
              I18n.t('js.quick_search.x_more_results', {
                count: extraSearchResultsCountsByType('Account'),
              })
            }}
          </div>
        </div>
      </div>
      <div
        v-if="searchResultsByType('Company').length > 0"
        class="
          sf-search-results sf-layer-white sf-layer-shadow
          search-form__results-section
        "
      >
        <div class="sf-tab sf-tab-icons sf-tab-full">
          <div v-if="currentCompanyId" class="sf-tab-label">
            {{ I18n.t('js.quick_search.companies') }}
          </div>
          <div
            v-for="company in searchResultsByType('Company')"
            :key="company.id"
            :class="activeSelectionClass(company)"
            class="sf-search-item sf-tab-item"
            @click="openItem(company, 'click')"
          >
            <a
              :href="urlForCompany(company)"
              class="sf-a sf-c-text sf-full-width"
              ><div class="sf-level sf-level-between sf-full-width">
                <div class="sf-level">
                  <div
                    v-if="company.personal_company"
                    v-tooltip="I18n.t('js.quick_search.personal_file')"
                    class="sf-tab-item-icon"
                  >
                    <span class="fa fa-user-circle"></span>
                  </div>
                  <span>{{ company.name }}</span>
                  <div
                    v-if="!company.visible_for_staff"
                    class="fa fa-shield"
                  ></div>
                </div>
                <div
                  :class="activeSelectionFileNumberClass(company)"
                  class="sf-level"
                >
                  {{ company.file_number }}
                </div>
              </div></a
            >
          </div>
          <div
            v-if="extraSearchResultsCountsByType('Company') > 0"
            class="sf-tab-item sf-bg-white sf-c-text sf-dim-el"
          >
            {{
              I18n.t('js.quick_search.x_more_results', {
                count: extraSearchResultsCountsByType('Company'),
              })
            }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import trackEvent from '@app/utils/track_event'

import { debounce } from 'lodash'

export default {
  name: 'QuickSearch',
  props: {
    isFullHeight: {
      type: Boolean,
      default: true,
    },
    isInitiallyFocused: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 4,
    },
    shouldFocusAfterMount: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focused: this.isInitiallyFocused,
      currentQueryInput: null,
      resultsStyle: 'auto',
    }
  },
  computed: {
    ...mapState(['currentCompanyId']),
    ...mapGetters([
      'searchResultsByType',
      'activeSelection',
      'isSelected',
      'extraSearchResultsCountsByType',
    ]),
  },

  beforeMount: function () {
    this.fetchSearchData()
    this.setResultLimit(this.limit)
  },

  mounted: function () {
    this.resultsStyle = this.calculateResultsStyle()

    if (this.shouldFocusAfterMount && !this.isInitiallyFocused) {
      this.focusInput()
    }
  },

  methods: {
    ...mapActions([
      'actualUpdateCurrentQuery',
      'clearCurrentQuery',
      'fetchSearchData',
      'moveSelectionDown',
      'moveSelectionUp',
      'setResultLimit',
    ]),
    calculateResultsStyle: function () {
      const container = this.$refs.container

      if (!container || this.isFullHeight) {
        return {
          height: 'auto',
        }
      }

      const searchBoxHeight = container.getBoundingClientRect().height
      const searchBoxDistanceToTop = container.getBoundingClientRect().top
      const marginAround = 48

      return {
        'max-height': `calc(100dvh - ${searchBoxHeight}px - ${searchBoxDistanceToTop}px - ${
          2 * marginAround
        }px)`,
        'overflow-y': 'auto',
      }
    },
    updateCurrentQuery: debounce(function () {
      this.actualUpdateCurrentQuery(this.currentQueryInput)
      this.emitSearchQueryUpdated(this.currentQueryInput)
    }, 500),
    emitSearchQueryUpdated: (input) => {
      window.eventHub.$emit('quickSearch:searchQueryUpdated', {
        empty: input === null || input.length === 0,
      })
    },
    // eslint-disable-next-line camelcase
    urlForCompany: ({ id, firm_id }) =>
      Silverfin.Routes.company_workflows_path({
        firm_id: firm_id,
        company_id: id,
      }),
    // eslint-disable-next-line camelcase
    urlForReconciliationText({ firm_id, company_id, ledger_id, id }) {
      return Silverfin.Routes.ledger_reconciliation_text_path({
        firm_id: firm_id,
        company_id: company_id,
        ledger_id: ledger_id,
        id: id,
        _options: true,
      })
    },
    // eslint-disable-next-line camelcase
    urlForAccount({ firm_id, company_id, ledger_id, number }) {
      return Silverfin.Routes.ledger_account_entry_path({
        firm_id: firm_id,
        account_number: number,
        company_id: company_id,
        ledger_id: ledger_id,
      })
    },
    // eslint-disable-next-line camelcase
    urlForWorkflow({ firm_id, company_id, ledger_id, id }) {
      return Silverfin.Routes.ledger_company_workflow_path(
        firm_id,
        company_id,
        ledger_id,
        id
      )
    },
    openItem(item, eventSource) {
      if (item && item.type) {
        trackEvent(
          'search',
          `open_${item.type.toLowerCase()}`,
          `via_${eventSource}`
        )

        if (item.type === 'Company') {
          Turbolinks.visit(this.urlForCompany(item))
        } else if (item.type === 'Account') {
          Turbolinks.visit(this.urlForAccount(item))
        } else if (item.type === 'ReconciliationText') {
          Turbolinks.visit(this.urlForReconciliationText(item))
        } else if (item.type === 'Workflow') {
          Turbolinks.visit(this.urlForWorkflow(item))
        }
      }
    },
    openActiveSelection() {
      this.openItem(this.activeSelection, 'shortcut')
    },
    activeSelectionClass(item) {
      if (this.isSelected(item)) {
        return 'sf-search-item-active sf-bg-primary-subtle'
      }
    },
    activeSelectionFileNumberClass(item) {
      return this.isSelected(item) ? '' : 'sf-c-text-light'
    },
    focusInput() {
      this.$refs.searchInput.focus()
      this.focused = true
    },
    inputBlurred() {
      this.focused = false
    },
    animationClass() {
      if (this.focused) {
        return 'sf-animation-zoom'
      }
    },
  },
}
</script>
<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.search-form {
  &__results-section {
    margin: 0 0 space(2);

    &:last-of-type {
      margin: 0;
    }
  }
}
</style>
